import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import logger from '../utils/logger';
import { validateMdsUrl } from '../utils/urlValidator';
import { deserializeMDSFile, saveProject, findProjectBySourceUrl, loadProject } from '../services/storageService';
import { initializeLoadedProject } from '../services/projectService';
import { useProjectStore } from '../stores/projectStore';
import { createProjectId } from '../../types';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface ExistingProjectInfo {
  filename: string;
  projectId?: string;
  projectName?: string;
  lastModified?: string;
  sourceUrl?: string;
  sourceUrlFetchedAt?: string;
}

interface MdsLoaderState {
  /** 読み込み中かどうか */
  isLoading: boolean;
  /** エラーメッセージ */
  error: string | null;
  /** 警告ダイアログを表示するか */
  showWarning: boolean;
  /** 警告対象のURL */
  warningUrl: string | null;
  /** 警告の理由 */
  warningReason: string | null;
  /** 既存プロジェクトの情報 */
  existingProject: ExistingProjectInfo | null;
  /** 既存プロジェクト確認ダイアログを表示するか */
  showExistingProjectDialog: boolean;
}

interface UseMdsLoaderOptions {
  /** フックを無効化する（埋め込みモードなど） */
  disabled?: boolean;
}

/**
 * URL経由のMDS読み込みフック
 *
 * URLパラメータ`mds`を検出し、MDSファイルを自動読み込みします。
 *
 * @example
 * ```
 * https://midas.app/?mds=https://example.com/project.mds
 * ```
 */
export function useMdsLoader(options: UseMdsLoaderOptions = {}) {
  const { disabled = false } = options;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<MdsLoaderState>({
    isLoading: false,
    error: null,
    showWarning: false,
    warningUrl: null,
    warningReason: null,
    existingProject: null,
    showExistingProjectDialog: false,
  });

  const loadMdsFromUrl = useCallback(async (url: string) => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null, showWarning: false }));

      logger.info('[useMdsLoader] Fetching MDS from URL:', url);

      // fetch()でMDSファイルを取得
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Content-Typeチェック（オプション、MIMEタイプが正しく設定されていない場合もある）
      const contentType = response.headers.get('Content-Type');
      logger.info('[useMdsLoader] Content-Type:', contentType);

      // ファイルサイズチェック
      const contentLength = response.headers.get('Content-Length');
      if (contentLength && parseInt(contentLength, 10) > MAX_FILE_SIZE) {
        throw new Error(`File size exceeds 10MB limit (${Math.round(parseInt(contentLength, 10) / 1024 / 1024)}MB)`);
      }

      const arrayBuffer = await response.arrayBuffer();

      // ダウンロード後のサイズチェック
      if (arrayBuffer.byteLength > MAX_FILE_SIZE) {
        throw new Error(`File size exceeds 10MB limit (${Math.round(arrayBuffer.byteLength / 1024 / 1024)}MB)`);
      }

      logger.info('[useMdsLoader] MDS file downloaded:', arrayBuffer.byteLength, 'bytes');

      const data = new Uint8Array(arrayBuffer);

      // デシリアライズ（gzip展開、JSONパース、マイグレーション適用）
      logger.info('[useMdsLoader] Deserializing MDS file...');
      const projectData = await deserializeMDSFile(data);

      // プロジェクトを初期化（完全な構造で）
      logger.info('[useMdsLoader] Initializing project...');
      const project = initializeLoadedProject(projectData);

      // URL経由で開いた場合は新しいプロジェクトIDを生成
      const newProjectId = createProjectId(`project-${crypto.randomUUID()}`);
      project.id = newProjectId;

      // sourceUrlとsourceUrlFetchedAtを設定
      project.sourceUrl = url;
      project.sourceUrlFetchedAt = new Date().toISOString();

      logger.info('[useMdsLoader] Project ID regenerated for URL-loaded project:', newProjectId);

      // ストアに保存
      useProjectStore.getState().loadProject(project);

      // OPFSに自動保存（リロード時の復元用）
      logger.info('[useMdsLoader] Auto-saving loaded project to OPFS...');
      await saveProject(project);

      logger.info('[useMdsLoader] MDS loaded successfully:', project.id);

      // プロジェクトビューに遷移
      navigate(`/project/${project.id}`);

    } catch (err) {
      logger.error('[useMdsLoader] Error loading MDS from URL:', err);

      let errorMessage = 'Failed to load MDS file from URL';

      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error: Failed to fetch the MDS file. Please check your internet connection or verify that the URL is accessible.';
      } else if (err instanceof Error) {
        if (err.message.includes('CORS')) {
          errorMessage = 'CORS error: The server does not allow loading this file from a browser. The server needs to set Access-Control-Allow-Origin headers.';
        } else {
          errorMessage = err.message;
        }
      }

      setState({
        isLoading: false,
        error: errorMessage,
        showWarning: false,
        warningUrl: null,
        warningReason: null,
        existingProject: null,
        showExistingProjectDialog: false,
      });
    }
  }, [navigate]);

  useEffect(() => {
    // 無効化されている場合は何もしない（埋め込みモードなど）
    if (disabled) {
      return;
    }

    const mdsUrl = searchParams.get('mds');

    if (!mdsUrl) {
      // mdsパラメータがない場合は何もしない
      return;
    }

    // URLパラメータが存在する場合、読み込みを開始
    logger.info('[useMdsLoader] MDS URL detected:', mdsUrl);

    // URL検証
    const validation = validateMdsUrl(mdsUrl);

    if (!validation.allowed) {
      // ブロック（メタデータエンドポイントなど）
      logger.error('[useMdsLoader] URL blocked:', validation.reason);
      setState({
        isLoading: false,
        error: validation.reason || 'URL is blocked for security reasons',
        showWarning: false,
        warningUrl: null,
        warningReason: null,
        existingProject: null,
        showExistingProjectDialog: false,
      });
      return;
    }

    // 警告が必要なURLの場合、ファイルサイズを事前チェック
    if (validation.needsWarning) {
      // HEADリクエストでContent-Lengthをチェック
      (async () => {
        // 3秒のタイムアウトを設定（プライベートIPへのリクエストが長時間ブロックされるのを防ぐ）
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), 3000);

        try {
          const headResponse = await fetch(mdsUrl, {
            method: 'HEAD',
            signal: abortController.signal
          });
          clearTimeout(timeoutId);
          const contentLength = headResponse.headers.get('Content-Length');

          if (contentLength && parseInt(contentLength, 10) > MAX_FILE_SIZE) {
            // ファイルサイズが制限を超えている場合、エラーを表示
            const sizeInMB = Math.round(parseInt(contentLength, 10) / 1024 / 1024);
            logger.error('[useMdsLoader] File size exceeds limit:', sizeInMB, 'MB');
            setState({
              isLoading: false,
              error: `File size exceeds 10MB limit (${sizeInMB}MB)`,
              showWarning: false,
              warningUrl: null,
              warningReason: null,
              existingProject: null,
              showExistingProjectDialog: false,
            });
            return;
          }
        } catch (err) {
          clearTimeout(timeoutId);
          // HEADリクエストが失敗した場合はスキップ（警告ダイアログを表示）
          logger.warn('[useMdsLoader] HEAD request failed, skipping file size check:', err);
        }

        // ファイルサイズチェックが通ったら警告ダイアログを表示
        logger.warn('[useMdsLoader] URL requires warning:', validation.reason);
        setState({
          isLoading: false,
          error: null,
          showWarning: true,
          warningUrl: mdsUrl,
          warningReason: validation.reason || 'This URL is not from a trusted source',
          existingProject: null,
          showExistingProjectDialog: false,
        });
      })();
      return;
    }

    // 既存プロジェクトをチェック
    (async () => {
      try {
        const existingProject = await findProjectBySourceUrl(mdsUrl);

        if (existingProject) {
          // 同じURLから開いたプロジェクトが既に存在する
          logger.info('[useMdsLoader] Found existing project from same URL:', existingProject);
          setState({
            isLoading: false,
            error: null,
            showWarning: false,
            warningUrl: null,
            warningReason: null,
            existingProject,
            showExistingProjectDialog: true,
          });
          return;
        }

        // 既存プロジェクトがない場合は、そのまま読み込み
        loadMdsFromUrl(mdsUrl);
      } catch (err) {
        logger.error('[useMdsLoader] Error checking existing project:', err);
        // エラーが発生しても続行
        loadMdsFromUrl(mdsUrl);
      }
    })();
  }, [disabled, searchParams, loadMdsFromUrl]);

  const handleCancelWarning = () => {
    logger.info('[useMdsLoader] User cancelled URL loading');
    setState({
      isLoading: false,
      error: null,
      showWarning: false,
      warningUrl: null,
      warningReason: null,
      existingProject: null,
      showExistingProjectDialog: false,
    });
    // ホーム画面に戻る（mdsパラメータをクリア）
    navigate('/');
  };

  const handleContinueWarning = () => {
    logger.info('[useMdsLoader] User accepted URL warning, continuing...');
    const { warningUrl } = state;
    if (warningUrl) {
      loadMdsFromUrl(warningUrl);
    }
  };

  const handleOpenExisting = useCallback(async () => {
    logger.info('[useMdsLoader] Opening existing project');
    const { existingProject } = state;
    if (!existingProject || !existingProject.filename) {
      logger.error('[useMdsLoader] No existing project to open');
      return;
    }

    try {
      // OPFSから既存プロジェクトを読み込み
      const project = await loadProject(existingProject.filename);

      if (!project) {
        throw new Error('Project not found');
      }

      // ストアに保存
      useProjectStore.getState().loadProject(project);

      // プロジェクトビューに遷移
      navigate(`/project/${project.id}`);
    } catch (err) {
      logger.error('[useMdsLoader] Error loading existing project:', err);
      setState((prev) => ({
        ...prev,
        error: 'Failed to load existing project',
        showExistingProjectDialog: false,
        existingProject: null,
      }));
    }
  }, [state, navigate]);

  const handleOpenNew = useCallback(() => {
    logger.info('[useMdsLoader] Opening as new project');
    const { existingProject } = state;
    if (!existingProject || !existingProject.sourceUrl) {
      logger.error('[useMdsLoader] No source URL to load');
      return;
    }

    // 確認ダイアログを閉じて、URLから新規プロジェクトとして読み込み
    setState((prev) => ({
      ...prev,
      showExistingProjectDialog: false,
      existingProject: null,
    }));
    loadMdsFromUrl(existingProject.sourceUrl);
  }, [state, loadMdsFromUrl]);

  const handleCancelExistingDialog = useCallback(() => {
    logger.info('[useMdsLoader] User cancelled existing project dialog');
    setState((prev) => ({
      ...prev,
      showExistingProjectDialog: false,
      existingProject: null,
    }));
    // ホーム画面に戻る（mdsパラメータをクリア）
    navigate('/');
  }, [navigate]);

  return {
    ...state,
    handleCancelWarning,
    handleContinueWarning,
    handleOpenExisting,
    handleOpenNew,
    handleCancelExistingDialog,
  };
}
